/**
 * @fileoverview Task Edit Hook
 *
 * Hook for editing existing tasks in the AutomationEditModal.
 * Manages task configuration state and provides methods to update and save changes.
 *
 * ARCHITECTURE:
 * 1. Load existing task from backend
 * 2. Convert to taskConfig using convertTaskToTaskConfig
 * 3. User modifies config via UI
 * 4. Save converts taskConfig to updates and calls updateTask API
 */

import { useState, useCallback, useEffect } from "react";
import cloudApi from "../../../../services/cloudApi";
import { convertTaskToTaskConfig, convertTaskConfigToUpdates } from "../utils/taskConfigConverters";
import { CONFIGURATIONS } from "../config/configurations";

/**
 * Hook for editing existing tasks
 *
 * @param {Object} options
 * @param {Object} options.task - The task object to edit (from getTask API or dashboard)
 * @param {Function} options.onSave - Callback when task is saved successfully
 * @returns {Object} Task editing state and actions
 */
const useTaskEdit = ({ task, onSave } = {}) => {
  const [taskConfig, setTaskConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize taskConfig from task when it becomes available
  useEffect(() => {
    if (task) {
      // Handle case where API returns { task: {...} } wrapper
      const taskData = task.task || task;

      console.log("📋 [useTaskEdit] Initializing task config from task:", taskData.id);
      console.log("📋 [useTaskEdit] Task data structure:", {
        id: taskData.id,
        task_type: taskData.task_type,
        hasRequest: !!taskData.request,
        requestKeys: taskData.request ? Object.keys(taskData.request) : [],
        configurationLength: taskData.request?.configuration?.length || 0,
      });

      const config = convertTaskToTaskConfig(taskData);
      console.log("📋 [useTaskEdit] Converted taskConfig:", config);

      setTaskConfig(config);
      setIsLoading(false);
      setHasUnsavedChanges(false);
    }
  }, [task]);

  /**
   * Update a specific section of the task config
   * @param {string} sectionKey - The key of the config section to update
   * @param {any} value - The new value for that section
   */
  const updateTaskSection = useCallback((sectionKey, value) => {
    setTaskConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [sectionKey]: value,
      };
    });
    setHasUnsavedChanges(true);
    setSaveError(null);
  }, []);

  /**
   * Update multiple sections at once
   * @param {Object} updates - Object with section keys and values
   */
  const updateMultipleSections = useCallback((updates) => {
    setTaskConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ...updates,
      };
    });
    setHasUnsavedChanges(true);
    setSaveError(null);
  }, []);

  /**
   * Reset a config section to its default value
   * @param {string} configId - The config ID to reset
   */
  const resetSectionToDefault = useCallback((configId) => {
    const configDef = CONFIGURATIONS.find((c) => c.id === configId);
    if (configDef) {
      updateTaskSection(configId, { ...configDef.defaultConfig });
    }
  }, [updateTaskSection]);

  /**
   * Reset all sections to their default values
   */
  const resetAllToDefaults = useCallback(() => {
    setTaskConfig((prev) => {
      if (!prev) return prev;

      const reset = { ...prev };
      CONFIGURATIONS.forEach((configDef) => {
        reset[configDef.id] = { ...configDef.defaultConfig };
      });
      return reset;
    });
    setHasUnsavedChanges(true);
    setSaveError(null);
  }, []);

  /**
   * Save the task config to the backend
   * @returns {Promise<Object>} The updated task
   */
  const saveTask = useCallback(async () => {
    if (!task || !taskConfig) {
      setSaveError("No task to save");
      return null;
    }

    if (isSaving) {
      return null;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      console.log("💾 [useTaskEdit] Saving task:", task.id);

      // Convert taskConfig to update payload
      const updates = convertTaskConfigToUpdates(taskConfig);
      console.log("📦 [useTaskEdit] Update payload:", updates);

      // Call API to update task
      const result = await cloudApi.updateTask(task.id, updates);
      console.log("✅ [useTaskEdit] Task saved successfully");

      setHasUnsavedChanges(false);

      // Notify parent component
      if (onSave) {
        onSave(result);
      }

      return result;
    } catch (error) {
      console.error("❌ [useTaskEdit] Failed to save task:", error);
      const message = error?.message || "Failed to save task. Please try again.";
      setSaveError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [task, taskConfig, isSaving, onSave]);

  /**
   * Get preview of the API payload (for debugging)
   * @returns {Object} The update payload that would be sent to the API
   */
  const getPayloadPreview = useCallback(() => {
    if (!taskConfig) return null;
    try {
      return convertTaskConfigToUpdates(taskConfig);
    } catch (error) {
      return { error: error.message };
    }
  }, [taskConfig]);

  /**
   * Discard unsaved changes and reload from original task
   */
  const discardChanges = useCallback(() => {
    if (task) {
      const taskData = task.task || task;
      const config = convertTaskToTaskConfig(taskData);
      setTaskConfig(config);
      setHasUnsavedChanges(false);
      setSaveError(null);
    }
  }, [task]);

  return {
    // State
    taskConfig,
    isLoading,
    isSaving,
    saveError,
    hasUnsavedChanges,

    // Actions
    updateTaskSection,
    updateMultipleSections,
    resetSectionToDefault,
    resetAllToDefaults,
    saveTask,
    discardChanges,
    getPayloadPreview,

    // Direct setter for advanced use cases
    setTaskConfig,
  };
};

export default useTaskEdit;
